import { useEffect, useRef, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import BoardHeader from './components/BoardHeader';
import BoardColumn from './components/BoardColmun';
import styled from 'styled-components';
import colorTheme from '../../constant/colors';
import useGetAllRequestSupport from './services/hooks/useGetAllRequestSupport';
import { ITicketSupport } from './interfaces/apis';
import TicketCard from './components/TicketCard';
import requestSupportApis from '../../services/requestSupportApis';
import ChangeAssignModal, { ChangeAssignModalRef } from './components/ChangeAssignModal';
import AddRequestModal from './components/AddRequestModal';
import DetailRequestModal, { DetailRequestModalRef } from './components/DetailRequestModal';

const SupportBoardPage = () => {
  const { data, refetch } = useGetAllRequestSupport();
  const [columns, setColumns] = useState(data || []);
  const [activeTicket, setActiveTicket] = useState<ITicketSupport | null>(null);
  const changeAssignModalRef = useRef<ChangeAssignModalRef>(null);
  const detailRequestModalRef = useRef<DetailRequestModalRef>(null);
  const [open, setOpen] = useState(false);
  
  useEffect(() => {
    setColumns(data || []);
  }, [data]);

  const findColumn = (id: string) => {
    const column = columns.find((column) => column.tickets.some((item) => item.id === id));
    if (column) return column;
    return columns.find((column) => column.status === id);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const ticket = event.active.data.current?.ticket;

    if (!ticket) return;

    setActiveTicket(ticket);
  };

  const handleDragCancel = () => {
    setActiveTicket(null);
  };

  const handleUpdateStatus = async (ticket: ITicketSupport, newStatus: string) => {
    try {
      await requestSupportApis.updateStatusRequestSupport(newStatus, ticket.id);
    } catch (error) {
      console.error('Error updating ticket status:', error);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    const activeColumn = findColumn(activeId);
    const overColumn = findColumn(overId);

    if (!activeColumn || !overColumn) return;

    // SAME COLUMN

    if (activeColumn.status === overColumn.status) {
      const activeItems = activeColumn.tickets;

      const oldIndex = activeItems.findIndex((i: ITicketSupport) => i.id === activeId);

      const newIndex = activeItems.findIndex((i: ITicketSupport) => i.id === overId);

      if (oldIndex === -1 || newIndex === -1) return;

      const newItems = arrayMove(activeItems, oldIndex, newIndex);

      const newColumns = columns.map((column) => {
        if (column.status === activeColumn.status) {
          return {
            ...column,
            tickets: newItems,
          };
        }

        return column;
      });

      setColumns(newColumns);
    } else {
      // DIFFERENT COLUMN

      const activeItems = activeColumn.tickets;

      const overItems = overColumn.tickets;

      const activeIndex = activeItems.findIndex((i: ITicketSupport) => i.id === activeId);

      const overIndex = overItems.findIndex((i: ITicketSupport) => i.id === overId);

      const movedItem = activeItems[activeIndex];

      // REMOVE OLD COLUMN
      const newActiveItems = activeItems.filter((i: ITicketSupport) => i.id !== activeId);

      // INSERT NEW COLUMN
      const newOverItems = [...overItems];

      const insertIndex = overIndex >= 0 ? overIndex : overItems.length;

      newOverItems.splice(insertIndex, 0, {
        ...movedItem,
      });

      const newColumns = columns.map((column) => {
        // ACTIVE
        if (column.status === activeColumn.status) {
          return {
            ...column,
            tickets: newActiveItems,
          };
        }

        // OVER
        if (column.status === overColumn.status) {
          return {
            ...column,
            tickets: newOverItems,
          };
        }

        return column;
      });

      setColumns(newColumns);

      handleUpdateStatus(movedItem, overColumn.status);
    }

    setActiveTicket(null);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleOpenChangeAssignModal = (assignId: string, ticketId: string) => {
    if (changeAssignModalRef.current) {
      changeAssignModalRef.current.open(assignId, ticketId);
    }
  };

  const handleOpenAddRequestModal = () => {
    setOpen(true);
  };

  const handleOpenDetailRequestModal = (ticketId: string) => {
    if (detailRequestModalRef.current) {
      detailRequestModalRef.current.open(ticketId);
    }
  };

  return (
    <Container>
      <BoardHeader refetch={refetch} />

      <DndContext
        onDragStart={handleDragStart}
        collisionDetection={pointerWithin}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        sensors={sensors}
      >
        <Content>
          {columns.map((column) => (
            <BoardColumn
              key={column.status}
              column={column}
              handleOpenChangeAssignModal={handleOpenChangeAssignModal}
              onOpenAddRequestModal={handleOpenAddRequestModal}
              onOpenDetailRequestModal={handleOpenDetailRequestModal}
            />
          ))}
        </Content>
        <DragOverlay dropAnimation={null}>{activeTicket ? <TicketCard ticket={activeTicket} /> : null}</DragOverlay>
      </DndContext>
      <ChangeAssignModal ref={changeAssignModalRef} refetch={refetch} />
      <AddRequestModal open={open} onClose={() => setOpen(false)} refetch={refetch} />
      <DetailRequestModal ref={detailRequestModalRef} refetch={refetch} />
    </Container>
  );
};

export default SupportBoardPage;

const Container = styled.div`
  width: 100%;
  height: calc(100vh - 64px);
  padding-bottom: 24px;
  background: ${colorTheme.fill_1};
`;

const Content = styled.div`
  width: 100%;
  height: calc(100% - 64px);
  overflow-y: hidden;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  padding: 16px 24px 0;
  min-height: 0;
`;
